import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import {
  Field,
  Image,
  ImageField,
  RichTextField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import gsap from 'gsap/dist/gsap';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { JabraIcon, mapSharedPropertiesToFields } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { withTheme } from 'common/jabraTheme/withTheme';
import { ComponentProps } from 'lib/component-props';

type EnterpriseBatterySpotRendering = {
  fields: EnterpriseBatterySpotFields;
};
export type EnterpriseBatterySpotFields = {
  blocks: EnterpriseBatterySpotBlock[];
  ksp?: KSPItem;
  hoursNumber: Field<number>;
  imageTopOffset: Field<number>;
  image: ImageField;
  eyebrow?: TextField;
  title?: TextField;
  description?: TextField;
};

export type KSPItem = {
  id: string;
  Title: Field<string>;
  Header: Field<string>;
  Text: Field<string>;
};
export type EnterpriseBatterySpotBlock = {
  id: string;
  headline: RichTextField;
  description: RichTextField;
  icon: Field<string>;
};
type EnterpriseBatterySpotProps = {
  rendering: EnterpriseBatterySpotRendering;
} & ComponentProps;

const EnterpriseBatterySpot = ({ rendering }: EnterpriseBatterySpotProps) => {
  const boxRef = useRef(null);
  const imageRef = useRef(null);
  const text1Ref = useRef(null);
  const text2Ref = useRef(null);
  const text3Ref = useRef(null);

  const data = mapSharedPropertiesToFields<EnterpriseBatterySpotFields>(rendering.fields);

  useGSAP(() => {
    const current = boxRef.current;

    const heroTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: current,
        start: 'top center',
        end: 'top top',
        scrub: 2,
      },
    });
    heroTimeline.from(imageRef.current, {
      scale: 1.5,
      ease: 'power1.inOut',
    });

    heroTimeline.from(
      text1Ref.current,
      {
        y: -200,
      },
      '<'
    );
    heroTimeline.from(
      text2Ref.current,
      {
        y: 200,
      },
      '<'
    );
    heroTimeline.from(
      text3Ref.current,
      {
        y: -200,
      },
      '<'
    );
  }, []);

  const imageOffset = data.imageTopOffset.value ?? 0;

  const numberString = data.hoursNumber.value?.toString();
  const [firstDigit, secondDigit, thirdDigit] = numberString?.split('') ?? [];

  const eyebrowText = data.ksp?.Title.value || data.eyebrow?.value;
  const header = data.ksp?.Header.value || data.title?.value;
  const text = data.ksp?.Text.value || data.description?.value;
  const blocks = data.blocks;
  const gridClass = `grid place-items-center justify-center grid-cols-1 xl:grid-cols-${Math.min(blocks.length, 4)}  md:grid-cols-${Math.min(blocks.length, 2)}`;
  return (
    <div
      ref={boxRef}
      className="mh-screen bg-theme relative flex w-full flex-col items-center overflow-hidden"
    >
      <div className="absolute inset-0 z-0 m-auto h-full w-full bg-[radial-gradient(circle,rgba(255,255,255,0.3)_0%,rgba(0,0,0,0)_50%)]" />
      {(eyebrowText || header || text) && (
        <div className="container mt-[6rem] flex max-w-6xl justify-center gap-6 px-3">
          <div className="flex w-full flex-col gap-10">
            {eyebrowText && (
              <div>
                <Eyebrow text={eyebrowText as string} className="items-center" />
              </div>
            )}

            {header && (
              <div className="flex w-full justify-center">
                <h2
                  className="text-center text-5xl font-bold leading-[52px]"
                  dangerouslySetInnerHTML={{ __html: sanitize(header as string) }}
                />
              </div>
            )}
            {text && (
              <div
                className="flex flex-col gap-[10px] text-center text-2xl font-normal leading-8"
                dangerouslySetInnerHTML={{ __html: sanitize(text as string) }}
              ></div>
            )}
          </div>
        </div>
      )}
      <div className="relative h-[min(70vw,50rem)] w-full select-none">
        <div className="flex w-full justify-center text-[min(80vw,50rem)] font-bold leading-[min(80vw,50rem)] text-black opacity-65">
          {!!firstDigit && <div ref={text1Ref}>{firstDigit}</div>}
          {!!secondDigit && <div ref={text2Ref}>{secondDigit}</div>}
          {!!thirdDigit && <div ref={text3Ref}>{thirdDigit}</div>}
        </div>
        <div
          className="absolute left-1/2 top-1/2 m-auto w-auto -translate-x-1/2 -translate-y-1/2 transform"
          ref={imageRef}
        >
          {data.image.value && (
            <Image
              style={{
                transform: `translateY(${imageOffset}px)`,
              }}
              field={data.image}
            />
          )}
        </div>
      </div>
      {blocks.length > 0 && (
        <div className="container">
          <div
            className={classNames(
              'mt-[4rem] w-full justify-center gap-4 py-6 xs:py-6 md:py-24 xl:py-6',
              gridClass
            )}
          >
            {blocks.map((block) => {
              const icon = (icons as any)[block.icon?.value];
              return (
                <div
                  key={block.id}
                  className="flex h-full w-full max-w-[800px] items-center gap-6 rounded-full bg-white bg-opacity-50 p-6 text-black"
                >
                  <div className="rounded-full bg-black/5 p-5">
                    {icon && <JabraIcon className="h-[2.5rem] w-[2.5rem]" Icon={icon} />}
                  </div>
                  <div className="pr-4">
                    <p
                      className="font-bold"
                      dangerouslySetInnerHTML={{
                        __html: sanitize(block.headline?.value as string),
                      }}
                    />
                    <p
                      dangerouslySetInnerHTML={{
                        __html: sanitize(block.description?.value as string),
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default withTheme({
  defaultTheme: {
    backgroundColor: '#627776',
    foregroundColor: 'white',
    darkTheme: true,
  },
})(withDatasourceCheck()<EnterpriseBatterySpotProps>(EnterpriseBatterySpot));
